import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import axios from 'axios';
import { Tag, Users, Building, Check, DollarSign } from 'lucide-react';

const PLAN_COLORS = {
  trial: 'border-slate-600 bg-slate-900',
  starter: 'border-blue-600 bg-blue-950/30',
  business: 'border-indigo-600 bg-indigo-950/30',
  enterprise: 'border-purple-600 bg-purple-950/30',
  enterprise_plus: 'border-amber-600 bg-amber-950/30',
};

const PLAN_BADGES = {
  trial: 'bg-slate-700 text-slate-200',
  starter: 'bg-blue-700 text-blue-100',
  business: 'bg-indigo-700 text-indigo-100',
  enterprise: 'bg-purple-700 text-purple-100',
  enterprise_plus: 'bg-amber-700 text-amber-100',
};

const SuperAdminPlans = ({ auth, onLogout }) => {
  const [plans, setPlans] = useState({});
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [loading, setLoading] = useState(true);

  const loadPlans = async () => {
    try {
      const res = await axios.get(`${API}/saas/plans`);
      setPlans(res.data || {});
    } catch {
      toast.error("Erreur chargement plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPlans(); }, []);

  const startEdit = (key, plan) => {
    setEditing(key);
    setEditValues({
      price_monthly: plan.price_monthly,
      price_yearly: plan.price_yearly,
      max_users: plan.max_users,
    });
  };

  const savePlan = async (key) => {
    try {
      await axios.put(`${API}/saas/plans/${key}`, editValues);
      toast.success("Plan mis a jour");
      setEditing(null);
      loadPlans();
    } catch {
      toast.error("Erreur sauvegarde");
    }
  };

  return (
    <SuperAdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-6 lg:p-8 space-y-8" data-testid="superadmin-plans">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestion des plans</h1>
          <p className="text-sm text-slate-400 mt-1">Configurez les tarifs et limites de chaque plan d'abonnement</p>
        </div>

        {loading ? (
          <div className="text-slate-400 text-center py-20">Chargement...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {Object.entries(plans).map(([key, plan]) => (
              <Card key={key} className={`p-6 border-2 ${PLAN_COLORS[key] || 'border-slate-700 bg-slate-900'}`} data-testid={`plan-${key}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${PLAN_BADGES[key] || 'bg-slate-700 text-white'}`}>
                    {plan.name}
                  </span>
                  {plan.on_demand && <span className="text-xs text-amber-400">Sur devis</span>}
                </div>

                {editing === key ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-400">Prix mensuel (CHF)</Label>
                      <Input type="number" value={editValues.price_monthly} onChange={e => setEditValues({...editValues, price_monthly: parseFloat(e.target.value) || 0})} className="bg-slate-800 border-slate-700 text-white" data-testid={`edit-monthly-${key}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-400">Prix annuel (CHF)</Label>
                      <Input type="number" value={editValues.price_yearly} onChange={e => setEditValues({...editValues, price_yearly: parseFloat(e.target.value) || 0})} className="bg-slate-800 border-slate-700 text-white" data-testid={`edit-yearly-${key}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-400">Max utilisateurs (-1 = illimite)</Label>
                      <Input type="number" value={editValues.max_users} onChange={e => setEditValues({...editValues, max_users: parseInt(e.target.value)})} className="bg-slate-800 border-slate-700 text-white" data-testid={`edit-users-${key}`} />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => savePlan(key)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`save-plan-${key}`}>
                        <Check className="w-3 h-3 mr-1" /> Sauvegarder
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="text-slate-400">Annuler</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 mb-5">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-400 text-sm">Mensuel</span>
                        <span className="text-white font-bold ml-auto">
                          {plan.on_demand ? 'Sur devis' : `CHF ${plan.price_monthly}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-400 text-sm">Annuel</span>
                        <span className="text-white font-bold ml-auto">
                          {plan.on_demand ? 'Sur devis' : `CHF ${plan.price_yearly}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-400 text-sm">Utilisateurs</span>
                        <span className="text-white font-bold ml-auto">
                          {plan.max_users === -1 ? 'Illimite' : plan.max_users}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-400 text-sm">Sites</span>
                        <span className="text-white font-bold ml-auto">
                          {plan.max_sites === -1 ? 'Illimite' : plan.max_sites}
                        </span>
                      </div>
                    </div>

                    <div className="border-t border-slate-800 pt-4 mb-4">
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Fonctionnalites</p>
                      <ul className="space-y-1.5">
                        {(plan.features || []).map((f, i) => (
                          <li key={i} className="text-xs text-slate-300 flex items-start gap-2">
                            <span className="text-emerald-500 mt-0.5">•</span> {f}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Button size="sm" variant="outline" onClick={() => startEdit(key, plan)} className="w-full border-slate-700 text-slate-300 hover:bg-slate-800" data-testid={`edit-plan-${key}`}>
                      Modifier les tarifs
                    </Button>
                  </>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminPlans;
